import { Card } from "react-bootstrap";
import * as React from 'react';
import { translateService } from "../../services/TranslateService";
import Translate from 'react-translate-component';
import styles from "./VehicleSearch.module.scss";
import Tippy from "@tippyjs/react";
import VehicleDetails from "./VehicleDetails";
import { useSelector } from "react-redux";
import { baseOnSalesPrice, basePrice } from "../MakeReservation/pricingCalculations";
import { cx, priceFormat } from "../MakeReservation/util";
import { useInView } from "react-intersection-observer";
import {GlobalStore} from "../../reduxStore";
import {VehicleImageSwiper} from "./VehicleImageSwiper";
import {useContext} from "react";
import {DataContext} from "../../contexts/dataContext";
import moment from 'moment';

type SetDetailsVisibleFunctionI = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
interface VehicleResultProps {
    vehicle: any;
    selectVehicle: Function;
    setRedirect: Function | null;
    totalPrice?: number;
    detailsVisible:boolean;
    setDetailsVisible: SetDetailsVisibleFunctionI;
    displaySticky?: boolean
    discount?: null |  number ;
}

interface PriceDisplayProps {
    totalPrice: number;
    discount: number ;
    displaySticky: boolean;
    available?: boolean;
}

export const PriceDisplay = ({totalPrice, discount, displaySticky=false, available=true}:PriceDisplayProps) => {
    return <>
        <div className={cx(displaySticky ? 'flex-column align-items-center' : 'flex-column align-items-center', 'd-flex justify-content-around')}>
            {(discount > 0 ) &&
                <div className={cx(styles.priceH4,styles.preferDiscountPrice)}>
                    {priceFormat(totalPrice)} €
                </div>}
            <div className={styles.priceH4}>
                {!available ?
                    <Translate content='vehicleSearch.reserved' />
                    :
                    discount ?
                        priceFormat(totalPrice-discount)+' € '
                        :
                        priceFormat(totalPrice)+' € '
                }
            </div>
            <div className={styles.includeVat}>
                <Translate with={{ type: moment().isBefore('2024-09-01') // before 1st Sept 2024 --> 24%, else 25.5% VAT
                        ? translateService.translate('vehicleSearch.vatPercent24') :
                        translateService.translate('vehicleSearch.vatPercent25')}}
                           content='vehicleSearch.vatIncluded'  />
            </div>
        </div>
    </>
}

const DefaultVehicleResult = (({ vehicle, selectVehicle, totalPrice = vehicle.onsale ? baseOnSalesPrice(vehicle) : basePrice(vehicle), detailsVisible,
                            setDetailsVisible, setRedirect=null, displaySticky=false }: VehicleResultProps) => {
    const dataContext = useContext(DataContext);
    const language = useSelector((state: GlobalStore) => state.chauffeurReducer.deviceLanguage);
    const reserved = !vehicle.availabilityData?.available;
    const [refFromScroll, refFromScrollVisible] = useInView();
    let price = totalPrice;

    if ((vehicle.vehicleModelData.ageLimit ?? 18 ) > 18) {
        vehicle.vehicleData.noundertwentyone = true;
    } else {
        vehicle.vehicleData.noundertwentyone = false;
    }

    let fuelType: string;
    if (+vehicle.vehicleData.fuelType === 0)
        fuelType = translateService.translate('vehicleSearch.diesel');
    else if (+vehicle.vehicleData.fuelType === 1)
        fuelType = translateService.translate('vehicleSearch.95E');
    else
        fuelType = translateService.translate('vehicleSearch.electric');

    if (vehicle.vehicleData.hybridity === 'normal')
        fuelType += " - "+ translateService.translate('vehicleSearch.hybrid');
    else if (vehicle.vehicleData.hybridity !== 'none')
        fuelType += " - "+ translateService.translate('vehicleSearch.plugin');

    const changeTwentyModal = () => {
        dataContext.changeTwentyModal(true)
        if(dataContext.twentyModal){
            dataContext.changeTwentyModal(false)
        }
    }

    return <>
        {(displaySticky && !refFromScrollVisible ) &&
            <div className={cx(styles.stickyPriceClass, styles.carPriceGreenBackground)}>
                <PriceDisplay totalPrice={price} discount={0} displaySticky={true} available={vehicle.availabilityData?.available} />
            </div>
        }

        <div className={'w-100'}>
            <Card className={`card-body my-3 p-3`}>
                <div className={'d-flex flex-lg-row flex-md-row flex-column'}>
                    <div className={`col-12 col-md-4 col-lg-4 px-0`} style={{height: "170px"}}>
                        <VehicleImageSwiper vehicle={vehicle}/>
                        {reserved &&
                            <div className={styles.carReserved}>
                                <div className={styles.label} style={{fontSize: '10px'}}>
                                    <Translate content='vehicleSearch.reserved'/>
                                </div>
                            </div>
                        }

                        {(!dataContext.oneWayRes && !vehicle.pricingData.withCoupon && vehicle.pricingData.base.unlimitedKilometers !== null &&
                                vehicle.pricingData.base.unlimitedKilometers < 1001 && !reserved) &&
                            <div className={styles.discountRibbon}>
                                <div className={styles.label}>
                                    <Translate
                                        content='vehicleSearch.unlimited'/> km {vehicle.pricingData?.base.unlimitedKilometers / 100} €/<Translate
                                    content='vehicleSearch.day'/>
                                </div>
                            </div>
                        }
                        {(!dataContext.oneWayRes && !vehicle.pricingData.withCoupon && vehicle.pricingData.base.unlimitedKilometers !== null &&
                                vehicle.pricingData.base.unlimitedKilometers > 1000 &&
                                vehicle.pricingData.base.additionalDriver !== 0 && !reserved) &&
                            <div className={styles.discountRibbon}>
                                <div className={styles.label}>
                                    <Translate
                                        content='vehicleSearch.unlimited'/> km {vehicle.pricingData?.base.unlimitedKilometers / 100} €/<Translate
                                    content='vehicleSearch.day'/>
                                </div>
                            </div>
                        }
                        {(!dataContext.oneWayRes && !vehicle.pricingData.withCoupon && vehicle.pricingData.base.additionalDriver === 0 &&
                                !(vehicle.pricingData.base.unlimitedKilometers < 1001) && !reserved) &&
                            <div className={styles.discountRibbon}>
                                <div className={styles.label}>
                                    <Translate content='vehicleSearch.additionalDriver'/> 0 €/<Translate
                                    content='vehicleSearch.day'/>
                                </div>
                            </div>
                        }
                    </div>
                    <div className={'col-12 col-md-6 col-lg-6 mt-sm-2 pl-lg-2 pl-md-2 px-0 '}>
                        <div className={`${styles.carModel} d-flex align-items-center justify-content-between flex-row`}>
                            {vehicle.vehicleModelData.manufacturer} {vehicle.vehicleModelData.name} {vehicle.vehicleModelData.dimensions.trunkVolume !== 0 ? vehicle.vehicleModelData.dimensions.trunkVolume + ' m³' : ''}
                        </div>
                        <div className={styles.carModelNeighborhood}>{vehicle.homeLocationData.neighborhood}</div>
                        <div className={styles.infoContainer}>
                            <div className={styles.addressContainer}>
                                {!dataContext.oneWayRes ?
                                    <span  className='d-flex flex-row'>
                                        <img width="19" height="23" className={styles.carFeatureIcon}  src={dataContext.rootUrl + '/assets/icons/new/map-pin-icon.svg'} alt="map-pin-icon" loading="lazy"/>
                                        <div className={'text-wrap'}>
                                            <span className={styles.carAddress}> {vehicle.homeLocationData.fullAddress} {Math.round((+vehicle.distance / 1000) * 10) / 10}km</span>
                                        </div>

                  </span>
                                    :
                                    <div className={styles.onewayAddresses}>
                    <span>
                      <img width="27" height="27" className={styles.carFeatureIcon} src={dataContext.rootUrl+'/assets/icons/new/search/icon-pickup-location.svg'} alt="icon-pickup-location" loading="lazy"/>
                      <span className={styles.carAddress} > {vehicle.homeLocationData.fullAddress} </span>
                      <span className={styles.carDistance} > {Math.round((+vehicle.distance / 1000) * 10) / 10} km</span>
                    </span>
                                        <span>
                                            <img width="27" height="27" className={styles.carFeatureIcon} src={dataContext.rootUrl+'/assets/icons/new/search/icon-return-location.svg'} alt="icon-return-location" loading="lazy"/>
                                            <span className={styles.carAddress} >{vehicle.returnLocationData?.fullAddress}</span>
                                        </span>
                                    </div>
                                }
                            </div>
                            <div>
                                {vehicle.parkingPermit.valid &&
                                    <>
                                        <div className={styles.r24Parking}>
                                            <img width="23" height="23" className={styles.carFeatureIcon} src={dataContext.rootUrl+"/assets/icons/new/search/icon-z-parking-permit.svg"} alt="icon-z-parking-permit" loading="lazy"/>
                                            <span className={styles.r24ParkingTxt}>
                                                <Translate content={'vehicleSearch.freeParking'} /> <span>{vehicle.vehicleData.parkingPermitCityName}</span>
                                            </span>
                                            <a className={`btn btn-sm btn-outline-info ${styles.greenReadMoreButton}`} target="_blank" rel="noopener noreferrer" href={language === 'fi' ? "https://www.24rent.fi/pysakointi-z-luvalla/" :
                                                "https://www.24rent.fi/en/parking-with-parking-permit-z/"}>
                                                <strong><Translate content={'vehicleSearch.readMore'} />...</strong>
                                            </a>
                                        </div>

                                    </>}
                            </div>

                            <div>
                                <div className={styles.iconAndText}>
                                    <Tippy
                                        className="tooltip-link"
                                        zIndex={2}
                                        content={<Translate content={'vehicleSearch.numberOfSeats'} />}
                                    >
                                        <img width="23" height="23" className={styles.carFeatureIcon} src={dataContext.rootUrl+"/assets/icons/new/car-feature-icons/icon-car-feature-seats.svg"} alt="icon-car-feature-seats" loading="lazy"/>
                                    </Tippy>
                                    <span className={styles.baseIconMargin}>{vehicle.vehicleData.seatCount}</span>
                                </div>
                                <div className={styles.iconAndText}>
                                    <Tippy
                                        className="tooltip-link"
                                        zIndex={2}
                                        content={<Translate content={'welcomePhase.vehicleInfoModal.transmission'} />}
                                    >
                                        <img width="23" height="23" className={styles.carFeatureIcon} src={dataContext.rootUrl+"/assets/icons/new/car-feature-icons/icon-car-feature-gears.svg"} alt="icon-car-feature-gears" loading="lazy"/>
                                    </Tippy>
                                    <span className={styles.baseIconMargin}>
                                        {!vehicle.vehicleData.hasAutomaticTransmission  ? <Translate content={'vehicleSearch.manual'} /> : <Translate content={'vehicleSearch.automatic'} />}
                                    </span>
                                </div>
                                <div className={styles.iconAndText}>
                                    <Tippy
                                        className="tooltip-link"
                                        zIndex={2}
                                        content={<Translate content={'welcomePhase.vehicleInfoModal.fuel'} />}
                                    >
                                        <img width="23" height="23" className={styles.carFeatureIcon} src={dataContext.rootUrl+"/assets/icons/new/car-feature-icons/icon-car-feature-fuel.svg"} alt="icon-car-feature-fuel" loading="lazy"/>
                                    </Tippy>
                                    <span className={styles.baseIconMargin}>
                                        {fuelType}
                                        {(vehicle.vehicleData.hybridity === 'normal' || vehicle.vehicleData.hybridity === 'plug-in' || +vehicle.vehicleData.fuelType === 2) &&
                                            <span className={styles.r24EcoText}> ECO</span>
                                        }
                                    </span>
                                </div>
                                {vehicle.vehicleData.hasHandtruck &&
                                    <div className={styles.iconAndText}>
                                        <Tippy
                                            className="tooltip-link"
                                            zIndex={2}
                                            content={<Translate content={'vehicleSearch.handtruck'} />}
                                        >
                                            <img width="23" height="23" className={styles.carFeatureIcon} src={dataContext.rootUrl+"/assets/icons/new/car-feature-icons/icon-car-feature-handtruck.svg"} alt="icon-car-feature-handtruck" loading="lazy"/>
                                        </Tippy>
                                        <span className={styles.baseIconMargin}> <Translate content={'vehicleSearch.handtruck'} /> </span>
                                    </div>
                                }

                                {vehicle.vehicleData.hasHitch &&
                                    <div className={styles.iconAndText}>
                                        <Tippy
                                            className="tooltip-link"
                                            zIndex={2}
                                            content={<Translate content={'vehicleSearch.towingHitch'} />}
                                        >
                                            <img width="23" height="23" className={styles.carFeatureIcon} src={dataContext.rootUrl+"/assets/icons/new/car-feature-icons/icon-car-feature-towbar.svg"} alt="icon-car-feature-towbar" loading="lazy"/>
                                        </Tippy>
                                        <span className={styles.baseIconMargin}> {vehicle.vehicleData.hitchPinCount} - <Translate content={'vehicleSearch.pins'} /></span>
                                    </div>
                                }
                                {vehicle.vehicleData.noundertwentyone ?
                                    <>
                                        <div className={styles.iconAndTextUnder21} onClick={() => changeTwentyModal()}>
                                        <Tippy
                                            className="tooltip-link"
                                            zIndex={2}
                                            content={<Translate content={'vehicleSearch.noundertwentyone'} />}
                                        >
                                            <img width="35" height="35"
                                                 style={{width:'35px',height:'35px'}}
                                                 src={dataContext.rootUrl+"/assets/icons/new/car-feature-icons/icon-car-feature-noundertwntyone.svg" }
                                                 alt={translateService.translate('vehicleSearch.noundertwentyone')} loading="lazy"/>
                                        </Tippy>
                                    </div>
                                    </> : null
                                }
                            </div>
                        </div>
                    </div>
                    <div className={cx(styles.r24ChildFlex1,styles.r24LeftMargin5)} ng-show="model.onSalesPrice == 0">
                        <div ref={refFromScroll} className={cx(styles.carPriceGreenBackground)}>
                            <PriceDisplay totalPrice={price} discount={0} displaySticky={false} available={true} />
                        </div>
                    </div>
                </div>
                <VehicleDetails vehicle={vehicle} detailsVisible={detailsVisible} setDetailsVisible={setDetailsVisible}>
                    {(setRedirect && !reserved && dataContext.route !== '/makereservation') &&
                        <button onClick={() => {selectVehicle(vehicle)}}
                                id={'defaultVehicleResultSelectButton'}
                                className="btn btn-primary btn-outline-primary mt-2 w-100 align-self-end rounded-3 font-weight-bold text-uppercase">
                            <Translate content={'vehicleSearch1.selectLocationAndDate'} />
                        </button>
                    }
                </VehicleDetails>

            </Card>
        </div>
    </>
});


export default DefaultVehicleResult;